function lazyLoadImages() {
  // Track which images are already being processed
  const processedImages = new Set();
  
  // Centralized image loading logic to avoid duplication
  function loadImage(lazyImage) {
    if (processedImages.has(lazyImage) || !lazyImage.hasAttribute('data-src')) return;
    
    processedImages.add(lazyImage);
    lazyImage.classList.add('loading-image');
    
    // Create a new image to test loading before applying to DOM element
    const tempImage = new Image();
    const imageSrc = lazyImage.getAttribute('data-src');
    const imageSrcset = lazyImage.getAttribute('data-srcset');
    
    // Handle load success
    tempImage.onload = () => {
      lazyImage.src = imageSrc;
      if (imageSrcset) {
        lazyImage.setAttribute('srcset', imageSrcset);
      }
      
      lazyImage.classList.remove('loading-image');
      lazyImage.classList.add('lazy-loaded');
      
      // Clean up attributes
      lazyImage.removeAttribute('data-src');
      if (imageSrcset) {
        lazyImage.removeAttribute('data-srcset');
      }
    };
    
    // Handle load failure with retry logic
    tempImage.onerror = () => {
      // If loading fails, retry after a delay (up to 3 times)
      if (!lazyImage.dataset.retryCount || parseInt(lazyImage.dataset.retryCount) < 3) {
        const retryCount = lazyImage.dataset.retryCount ? parseInt(lazyImage.dataset.retryCount) + 1 : 1;
        lazyImage.dataset.retryCount = retryCount;
        
        setTimeout(() => {
          processedImages.delete(lazyImage);
          loadImage(lazyImage);
        }, 1000 * retryCount); // Exponential backoff
      } else {
        // After 3 retries, mark as error and potentially load a fallback
        lazyImage.classList.remove('loading-image');
        lazyImage.classList.add('lazy-load-error');
        
        // Optionally set a fallback image
        lazyImage.src = lazyImage.getAttribute('data-fallback') || 
                        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%23ccc" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/%3E%3C/svg%3E';
      }
    };
    
    // Start loading the image
    tempImage.src = imageSrc;
  }

  // Setup event-based loading (hover/touch)
  function setupLazyLoading(lazyImage) {
    if (lazyImage.classList.contains('lazy-loaded')) return;

    const triggerLoad = () => {
      loadImage(lazyImage);
      
      // Remove event listeners once loading is triggered
      lazyImage.removeEventListener('mouseenter', triggerLoad);
      lazyImage.removeEventListener('touchstart', triggerLoad);
    };
    
    lazyImage.addEventListener('mouseenter', triggerLoad);
    lazyImage.addEventListener('touchstart', triggerLoad);
    
    return triggerLoad;
  }
  
  // Set up Intersection Observer to load images when they come into view
  function observeImages(container) {
    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver support
      const lazyImages = container.querySelectorAll('img[data-src]:not(.lazy-loaded)');
      lazyImages.forEach(loadImage);
      return;
    }
    
    const observerOptions = {
      root: null,
      rootMargin: '50px', // Preload images a bit before they come into view
      threshold: 0.1
    };
    
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const lazyImage = entry.target;
          loadImage(lazyImage);
          observer.unobserve(lazyImage);
        }
      });
    }, observerOptions);
    
    const lazyImages = container.querySelectorAll('img[data-src]:not(.lazy-loaded)');
    lazyImages.forEach(image => {
      imageObserver.observe(image);
    });
  }
  
  // Initialize lazy loading
  function initLazyLoad() {
    // Handle existing images
    observeImages(document.body);
    
    // Set up MutationObserver to handle dynamically added content
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldRefresh = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if it's a container that might contain images
              if (node.querySelector('img[data-src]')) {
                shouldRefresh = true;
              }
              
              // Check if it's an image itself
              if (node.tagName === 'IMG' && node.hasAttribute('data-src')) {
                shouldRefresh = true;
              }
              
              // Check for dynamic containers that might be added
              const dynamicContainer = node.closest('.drawer, .modal, .popup, [data-lazy-container]');
              if (dynamicContainer) {
                observeImages(dynamicContainer);
              }
            }
          });
        } else if (mutation.type === 'attributes') {
          // Handle cases where data-src is added to existing elements
          if (mutation.attributeName === 'data-src') {
            const target = mutation.target;
            if (target.tagName === 'IMG' && !target.classList.contains('lazy-loaded')) {
              shouldRefresh = true;
            }
          }
        }
      });
      
      // Refresh observer if new lazyload elements were added
      if (shouldRefresh) {
        observeImages(document.body);
      }
    });
    
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-src']
    });
  }
  
  // Handle page visibility changes (continue loading when tab becomes active)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      reloadPendingImages();
    }
  });
  
  // Handle network status changes
  window.addEventListener('online', reloadPendingImages);
  
  // Reload any pending images
  function reloadPendingImages() {
    const pendingImages = document.querySelectorAll('img[data-src]:not(.lazy-loaded)');
    pendingImages.forEach(loadImage);
  }
  
  // Public API
  return {
    init: initLazyLoad,
    loadImage: loadImage,
    reloadAll: reloadPendingImages,
    // Force reload of all images, including those already processed
    forceReload: () => {
      processedImages.clear();
      const allImages = document.querySelectorAll('img[data-src]');
      allImages.forEach(img => {
        img.classList.remove('lazy-loaded', 'lazy-load-error');
        loadImage(img);
      });
    }
  };
}

const LazyLoader = (() => {
  const loader = lazyLoadImages();
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loader.init);
  } else {
    loader.init();
  }
  
  document.addEventListener('ajaxComplete', loader.init);
  document.addEventListener('contentUpdated', loader.init);
  
  window.addEventListener('error', function(event) {
    if (event.target.tagName === 'IMG') {
      if (event.target.hasAttribute('data-src') && !event.target.classList.contains('lazy-load-error')) {
        setTimeout(() => {
          loader.loadImage(event.target);
        }, 1000);
      }
    }
  }, true);
  
  return loader;
})();

document.addEventListener('click', function() {
  setTimeout(LazyLoader.init, 100); 
});

window.LazyLoader = LazyLoader;